/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { twMerge } from 'utilities/src/twMerge';

import IconComponent from '../../../../Icon';
import VList from '../../../../VirtualizedList';

import { IOptionsProps } from './_props';

import useInputOptionsTheme from './themes';

const getAvailableViewportHeight = (element: HTMLDivElement) => {
  const viewport = window.visualViewport;
  const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;

  return Math.max(0, Math.floor(viewportBottom - element.getBoundingClientRect().top));
};

/**
 * Options Component
 */

function Options({
  open,
  options,
  valueText,
  iconPosition,
  highlightOptions,
  highlightHelperText,
  className,
  containerHeight: containerHeightProp,
  getItemProps,
  hoveredItemIndex,
  selectedItem,
}: Omit<IOptionsProps, 'getMenuProps'>) {
  const optionsPanelRef = React.useRef<HTMLDivElement>(null);
  const [viewportContainerHeight, setViewportContainerHeight] = React.useState(192);
  const {
    optionsWrapperThemeClasses,
    optionThemeClasses,
    optionActiveThemeClasses,
    optionSelectedThemeClasses,
    optionIconThemeClasses,
    optionHighlightedTextThemeClasses,
  } = useInputOptionsTheme();

  const setOptionsPanelRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      optionsPanelRef.current = element;

      if (element && containerHeightProp === 'viewport') {
        setViewportContainerHeight(getAvailableViewportHeight(element));
      }
    },
    [containerHeightProp],
  );

  React.useEffect(() => {
    if (containerHeightProp !== 'viewport') return undefined;

    const updateContainerHeight = () => {
      if (optionsPanelRef.current) {
        setViewportContainerHeight(getAvailableViewportHeight(optionsPanelRef.current));
      }
    };

    window.addEventListener('resize', updateContainerHeight);
    window.visualViewport?.addEventListener('resize', updateContainerHeight);
    window.visualViewport?.addEventListener('scroll', updateContainerHeight);

    return () => {
      window.removeEventListener('resize', updateContainerHeight);
      window.visualViewport?.removeEventListener('resize', updateContainerHeight);
      window.visualViewport?.removeEventListener('scroll', updateContainerHeight);
    };
  }, [containerHeightProp]);

  if (!open) return null;
  if (!options.length) return null;

  const highlightIndexes =
    highlightOptions && valueText
      ? options.map((opt) => opt.label.toLowerCase().indexOf(valueText.toLowerCase()))
      : [];

  const dynamicHeight = options.length * 36 + 12;
  const requestedContainerHeight =
    containerHeightProp === 'viewport' ? viewportContainerHeight : containerHeightProp;
  const containerHeight = requestedContainerHeight ?? Math.min(dynamicHeight, 192);

  return (
    <div
      ref={setOptionsPanelRef}
      className={twMerge(optionsWrapperThemeClasses, className)}
      data-testid="options-panel"
      style={requestedContainerHeight === undefined ? undefined : { maxHeight: requestedContainerHeight }}
    >
      <VList
        containerHeight={containerHeight}
        containerWidth="100%"
        eachItemSize={36}
        totalItemsCount={options.length}
        paintedItemsAfterOverflow={5}
        topOffset={12}
      >
        {({ index, style }) => {
          const option = options[index];

          return (
            <li
              key={`option-${option.value}`}
              data-testid={`option-${option.value}`}
              className={twMerge(
                optionThemeClasses,
                hoveredItemIndex === index && optionActiveThemeClasses,
                hoveredItemIndex === index && highlightHelperText && 'bg-site-accent-25 text-site-accent-700',
                selectedItem?.value === option.value && optionSelectedThemeClasses,
                {
                  'flex-row-reverse': iconPosition === 'right',
                },
              )}
              {...getItemProps({ item: option, index })}
              style={style}
            >
              <IconComponent iconName={option.icon} size="IconS" className={optionIconThemeClasses} />

              <span className="flex-1">
                {highlightOptions && valueText && highlightIndexes[index] >= 0 ? (
                  <>
                    {option.label.substring(0, highlightIndexes[index])}

                    <span className={optionHighlightedTextThemeClasses}>
                      {option.label.substring(highlightIndexes[index], highlightIndexes[index] + valueText.length)}
                    </span>

                    {option.label.substring(valueText.length + highlightIndexes[index])}
                  </>
                ) : (
                  option.label
                )}
              </span>

              {highlightHelperText && hoveredItemIndex === index && (
                <span className="text-site-accent-700 site-paragraph-s border-site-accent-200 rounded-sm border px-1.5 text-[0.625rem] font-bold uppercase tracking-[2%]">
                  {highlightHelperText}
                </span>
              )}
            </li>
          );
        }}
      </VList>
    </div>
  );
}

function OptionsWrapper({ getMenuProps, ...optionsProps }: IOptionsProps) {
  return (
    <ul {...getMenuProps()}>
      <Options {...optionsProps} />
    </ul>
  );
}

export default OptionsWrapper;
